import { gstinProblem, stateLabel } from './gst';
import { i18n } from './i18n';

/**
 * Checks the India GST forms make before calling the API, so the accountant sees them on the
 * field rather than as a refusal: Company settings › GST registration (document 06 T9) and the
 * e-way bill dialogs (T10). The API repeats every one of them.
 */

export interface GstRegistrationDraft {
  gstin: string;
  stateCode: string;
  registrationType: string;
}

/** GSTIN with its check character, the state it was issued in, and a registered type. */
export function gstRegistrationErrors(d: GstRegistrationDraft): Record<string, string> {
  const gstin = d.gstin.replace(/\s+/g, '').toUpperCase();
  if (!gstin) return {};
  const problem = gstinProblem(gstin);
  if (problem) return { gstin: problem };
  const e: Record<string, string> = {};
  if (gstin.slice(0, 2) !== d.stateCode)
    e['stateCode'] = i18n.t('taxPages.gstForms.gstinState', {
      state: stateLabel(gstin.slice(0, 2)),
    });
  if (d.registrationType === 'unregistered')
    e['registrationType'] = i18n.t('taxPages.gstForms.chooseRegistration');
  return e;
}

/**
 * Registration type to show once a GSTIN is typed: an unregistered company that adds its GSTIN
 * becomes regular unless the accountant picks another type.
 */
export function registrationTypeFor(gstin: string, current: string): string {
  return gstin.replace(/\s+/g, '') && current === 'unregistered' ? 'regular' : current;
}

/** The portal's limit on approximate distance (tax pack EWB_MAX_DISTANCE_KM). */
export const EWB_MAX_DISTANCE_KM = 4000;

const distanceProblem = (km: number) =>
  km > EWB_MAX_DISTANCE_KM
    ? i18n.t('taxPages.gstForms.maxDistance', { km: EWB_MAX_DISTANCE_KM })
    : null;

export interface EwbGenerateDraft {
  distanceKm: number;
  transMode: number | null;
  vehicleNo: string | null;
  transDocNo: string | null;
  transDocDate: string | null;
  transporterId: string | null;
  transporterName: string | null;
}

/**
 * The transport part of Generate e-way bill as the API takes it: the vehicle by road, the
 * transport document otherwise, and no mode at all for Part A only.
 */
export function ewbTransportBody<T extends EwbGenerateDraft>(t: T): T {
  const road = (t.transMode ?? 1) === 1;
  const vehicleNo = road ? t.vehicleNo?.trim() || null : null;
  const transDocNo = road ? null : t.transDocNo?.trim() || null;
  return {
    ...t,
    vehicleNo,
    transDocNo,
    transDocDate: road ? null : t.transDocDate || null,
    transporterId: t.transporterId?.trim() || null,
    transporterName: t.transporterName?.trim() || null,
    // Part A only: the transporter adds the vehicle later
    transMode: vehicleNo || transDocNo ? (t.transMode ?? 1) : null,
  };
}

/** Field problems in the Generate dialog the API would refuse (the IRP rules run there too). */
export function ewbGenerateErrors(t: EwbGenerateDraft): string[] {
  const body = ewbTransportBody(t);
  const out: string[] = [];
  const d = distanceProblem(t.distanceKm);
  if (d) out.push(d);
  if (body.transDocNo && !body.transDocDate) out.push(i18n.t('taxPages.gstForms.transDocDate'));
  if (!body.vehicleNo && !body.transDocNo && !body.transporterId)
    out.push(i18n.t('taxPages.gstForms.vehicleOrTransporter'));
  if (body.transporterName && body.transporterName.length > 100)
    out.push(i18n.t('taxPages.gstForms.transporterNameLength'));
  return out;
}

export interface EwbVehicleDraft {
  transMode: number;
  vehicleNo: string;
  transDocNo: string;
  fromPlace: string;
  fromState: string;
  distanceKm: number;
}

/** Field problems in Update vehicle (Part B). */
export function ewbVehicleErrors(u: EwbVehicleDraft): string[] {
  const out: string[] = [];
  if (u.transMode === 1 && !u.vehicleNo.trim()) out.push(i18n.t('taxPages.gstForms.vehicleNo'));
  if (u.transMode !== 1 && !u.transDocNo.trim()) out.push(i18n.t('taxPages.gstForms.transDocNo'));
  if (u.fromPlace.trim().length < 2) out.push(i18n.t('taxPages.gstForms.fromPlace'));
  if (!/^[0-9]{2}$/.test(u.fromState)) out.push(i18n.t('taxPages.gstForms.fromState'));
  const d = distanceProblem(u.distanceKm);
  if (d) out.push(d);
  return out;
}
